'use client';

import Link from 'next/link';
import styles from './QuestionnaireNavigationItem.module.css';
import type { QuestionnaireNavigationItemProps } from './QuestionnaireNavigationItem.types';
import type { Question } from '@/_types/models/questionnaire';
import type { Section, SectionState } from '@/_types/models/sections';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { Flex, Icon, Pill, Text } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { IconType } from '@/_types/icons';
import { getClasses } from '@/_utils/classnames';
import { useContext, useMemo, type FC } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { getSectionStatus } from '@/_utils/sections';
import { getQuestionsForSection } from '@/_utils/questions';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

const STATUS_ICON_MAP: Record<SectionState, IconType> = {
  completed: 'check-circle-filled',
  error: 'warning',
  pending: 'clock',
  incomplete: 'circle',
};

export const QuestionnaireNavigationItem: FC<
  QuestionnaireNavigationItemProps
> = ({ mode, section }) => {
  const {
    selectedQuestionnaire,
    selectedSection: sSection,
    selectedQuestions: sQuestions,
    selectedSections,
  } = useContext(ClientQuestionnaireContext);
  const { previewQuestionnaire, previewSection, previewSections } =
    useContext(PreviewContext);
  const { previewTemplateSection, previewTemplate } =
    useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);

  const currentSection: Partial<Section> | undefined = useMemo(() => {
    if (mode === 'client') return sSection;
    if (mode === 'admin-preview') return previewSection;
    if (mode === 'admin-template') return previewTemplateSection;

    return sSection;
  }, [mode, sSection, previewTemplateSection, previewSection]);

  const currentQuestions: Array<Question> = useMemo(() => {
    if (mode === 'admin-preview') {
      const hasMultipleSections: boolean = Boolean(
        (previewSections?.length ?? 0) > 1,
      );

      return getQuestionsForSection(
        previewQuestionnaire?.questionnaire_questions || [],
        section?.section_type,
        previewQuestionnaire,
        hasMultipleSections &&
          preferences?.hide_document_uploads_within_sections,
      );
    }

    if (mode === 'admin-template') {
      const hasMultipleSections: boolean = Boolean(
        (previewTemplate?.sections?.length ?? 0) > 1,
      );

      return getQuestionsForSection(
        previewTemplate?.questionnaire_questions || [],
        section?.section_type,
        previewTemplate,
        hasMultipleSections &&
          preferences?.hide_document_uploads_within_sections,
      );
    }

    const hasMultipleSections: boolean = Boolean(
      (selectedSections?.length ?? 0) > 1,
    );

    return getQuestionsForSection(
      sQuestions,
      section?.section_type,
      selectedQuestionnaire,
      hasMultipleSections && preferences?.hide_document_uploads_within_sections,
    );
  }, [
    previewQuestionnaire,
    previewTemplate,
    selectedQuestionnaire,
    sQuestions,
    mode,
    section?.section_type,
    selectedSections?.length,
    previewSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const currentSections = useMemo(() => {
    if (mode === 'client') return selectedQuestionnaire?.sections || [];
    if (mode === 'admin-preview') {
      return previewQuestionnaire?.sections || [];
    }
    if (mode === 'admin-template') {
      return previewTemplate?.sections || [];
    }
    return selectedQuestionnaire?.sections || [];
  }, [selectedQuestionnaire, previewQuestionnaire, previewTemplate, mode]);

  const currentSectionIndex = useMemo(() => {
    return (
      currentSections.findIndex(
        (section) => section.id === currentSection?.id,
      ) || 0
    );
  }, [currentSections, currentSection]);

  const { companyId, questionnaireId } = useParams();
  const sparams = useSearchParams();
  const questionnaireIdFromSearchParams = sparams.get('questionnaireId');
  const { selectedCompanyHash } = useContext(UserContext);

  const isActive: boolean = Boolean(
    currentSection && section?.id === currentSection?.id,
  );
  const status: SectionState = getSectionStatus(currentQuestions, section);

  const questionnaireNavigationItemClasses = getClasses(
    styles.questionnaireNavigationItem,
    {
      [styles.questionnaireNavigationItemActive]: isActive,
      [styles.questionnaireNavigationItemCompleted]: status === 'completed',
      [styles.questionnaireNavigationItemIncomplete]: status === 'incomplete',
      [styles.questionnaireNavigationItemError]: status === 'error',
    },
  );

  const isFirstSectionRequired: boolean = useMemo(() => {
    return Boolean(
      preferences?.first_section_required &&
        currentSectionIndex === 0 &&
        !currentSection?.marked_completed &&
        currentSections?.[0]?.section_key !== 'custom',
    );
  }, [preferences, currentSectionIndex, currentSection, currentSections]);

  const sectionLink: string = useMemo(() => {
    const sectionId: number = isFirstSectionRequired
      ? currentSections?.[0]?.id || section?.id
      : section?.id;

    if (mode === 'admin-preview') {
      return `/preview/${companyId}/questionnaire/${questionnaireId}?sectionId=${sectionId}`;
    }
    if (mode === 'admin-template') {
      return `/preview/template/${previewTemplate?.id}?sectionId=${sectionId}`;
    }

    return `/questionnaire/${selectedCompanyHash}/?questionnaireId=${selectedQuestionnaire?.id || questionnaireIdFromSearchParams}&sectionId=${sectionId}`;
  }, [
    companyId,
    mode,
    previewTemplate?.id,
    questionnaireId,
    questionnaireIdFromSearchParams,
    section?.id,
    selectedCompanyHash,
    selectedQuestionnaire?.id,
    isFirstSectionRequired,
    currentSections,
  ]);

  return (
    <div data-testid="questionnaire-navigation-item">
      <Link href={sectionLink}>
        <Flex
          className={questionnaireNavigationItemClasses}
          isYCentered={true}
          padding={4}
          gap={4}
        >
          <Icon
            size={20}
            name={STATUS_ICON_MAP[status]}
            className={styles.questionnaireNavigationItem__icon}
          />
          <Text type="small" fontWeight="normal">
            {section?.name}
          </Text>
          {isFirstSectionRequired &&
            section?.id === currentSections?.[0]?.id && (
              <Pill label="Required" intent="info" />
            )}
          {isFirstSectionRequired &&
            section?.id !== currentSections?.[0]?.id && (
              <Icon name="lock" size={12} />
            )}
        </Flex>
      </Link>
    </div>
  );
};
